// Pure TypeScript functions for picto size calculations

import { ARBITRARY_PICTO_SIZE, FLOOR_PLAN_CONFIG } from './constants';
import { IScaleParams } from '../types';

/**
 * Scales the picto size based on resource type map icon size
 *
 * This code is from J1:
 * ```
 * var scaledMapIconSize = resourceType.mapIconSize * $("#overlay-img img").width() / 900;
 * ```
 * On mobile, the code is:
 * ```
 * PIXELS_PER_METER / scalePixelsPerMeter * mapIconSize
 * ```
 * but this is a constant, which does not account for the windows' width
 *
 * @param mapIconSize - The base icon size from resource type
 * @param naturalWidth - Natural width of the background image
 * @param useFixedSize - Whether to use fixed size (arbitrary)
 * @returns The scaled picto size in pixels
 */
export const scalePictoSize = (mapIconSize: number, naturalWidth: number, useFixedSize = false): number => {
  if (useFixedSize) {
    return ARBITRARY_PICTO_SIZE;
  }

  return 2 * mapIconSize * (naturalWidth / FLOOR_PLAN_CONFIG.REFERENCE_WIDTH);
};

/**
 * Calculates grid square size based on image dimensions and scale
 *
 * @param scaleParams - Scaling parameters
 * @returns Grid square size in pixels
 */
export const calculateGridSquareSize = (scaleParams: IScaleParams): number => {
  const { naturalWidth, scalePixelsPerMeter } = scaleParams;

  const imgWidth = (naturalWidth * FLOOR_PLAN_CONFIG.PIXELS_PER_METER) / scalePixelsPerMeter;
  const ratio = imgWidth / naturalWidth;

  return FLOOR_PLAN_CONFIG.PIXELS_PER_CASE / ratio;
};

/**
 * Calculates translation offsets to center a picto within a grid cell
 *
 * @param pictoSize - Size of the picto
 * @param gridSquareSize - Size of the grid square
 * @returns Translation offsets { tx, ty }
 */
export const calculateCenteringOffset = (pictoSize: number, gridSquareSize: number): { tx: number; ty: number } => {
  if (pictoSize <= gridSquareSize) {
    // Picto is smaller than grid, center it
    const offset = gridSquareSize / 2 - pictoSize / 2;
    return { tx: offset, ty: offset };
  } else {
    // Picto is larger than grid, offset to keep center aligned
    const offset = -(pictoSize / 2 - gridSquareSize / 2);
    return { tx: offset, ty: offset };
  }
};

/**
 * Calculates translation offsets for pin-style pictos (location dots)
 * The tip of the pin should be in the center of the grid square
 *
 * @param gridSquareSize - Size of the grid square
 * @returns Translation offsets { tx, ty }
 */
export const calculatePinOffset = (gridSquareSize: number): { tx: number; ty: number } => {
  const tx = -(ARBITRARY_PICTO_SIZE / 2 - gridSquareSize / 2);
  const ty = -(ARBITRARY_PICTO_SIZE - gridSquareSize / 2);

  return { tx, ty };
};
